/**
 * V37.72 — Widget "Vos contrats CCMI" pour les membres.
 *
 * Affiche les projets CCMI dont le membre est le maitre d'ouvrage
 * (filtre serveur sur `maitre_ouvrage.email == JWT user.email`).
 *
 * V37.76 — Choix de paiement (Tache 3 e1) : deux boutons cote a cote
 *  - "Payer par carte (Stripe)"  -> Stripe Checkout (flux historique)
 *  - "Payer avec mes crédits"    -> debit credits (solde affiche)
 *
 * Une fois paye et le PDF genere par l'admin, le membre peut le
 * telecharger directement depuis cette carte.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, FileText, CreditCard, Download, Home, Coins } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUT_COLOR = {
  brouillon: 'bg-slate-600 text-white border-slate-800',
  analyse_en_cours: 'bg-amber-600 text-white border-amber-800',
  conforme: 'bg-emerald-700 text-white border-emerald-900',
  non_conforme: 'bg-red-700 text-white border-red-900',
  transmis_domium: 'bg-blue-700 text-white border-blue-900',
};

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function MesContratsCcmi() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyPay, setBusyPay] = useState(null);
  const [busyPayCredits, setBusyPayCredits] = useState(null);
  const [busyDownload, setBusyDownload] = useState(null);
  // V37.76 — options de paiement (credits) pour le service final_contract
  const [payOptions, setPayOptions] = useState(null);

  const fetchList = async () => {
    try {
      const res = await axios.get(`${API}/ccmi/my-projects`, { headers: getAuthHeader() });
      setItems(res.data?.items || []);
    } catch {
      // silencieux : pas un blocage du dashboard
    } finally {
      setLoading(false);
    }
  };

  const fetchPayOptions = async () => {
    try {
      const res = await axios.get(
        `${API}/legalcoop/payment-options?service_type=final_contract`,
        { headers: getAuthHeader() },
      );
      setPayOptions(res.data);
    } catch {
      // silencieux : le bouton credits n'est simplement pas affiche
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (mounted) {
        await fetchList();
        await fetchPayOptions();
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Cree (ou reutilise) la demande LEGALCOOP service=final_contract du projet
  const ensureRequestId = async (project) => {
    let requestId = project?.final_contract?.pending_request_id;
    if (!requestId) {
      const createRes = await axios.post(
        `${API}/legalcoop/requests`,
        {
          ccmi_project_id: project.id,
          service_type: 'final_contract',
          note: `Paiement direct membre — Contrat CCMI definitif ${project.reference}`,
        },
        { headers: getAuthHeader() },
      );
      requestId = createRes.data?.request?.id;
    }
    return requestId;
  };

  // Paiement carte : demande LEGALCOOP puis Stripe Checkout.
  const handlePayDirect = async (project) => {
    setBusyPay(project.id);
    try {
      const requestId = await ensureRequestId(project);
      if (!requestId) {
        toast.error('Impossible de créer la demande de paiement');
        return;
      }
      const checkoutRes = await axios.post(
        `${API}/legalcoop/requests/${requestId}/stripe-checkout`,
        { origin_url: window.location.origin },
        { headers: getAuthHeader() },
      );
      const url = checkoutRes.data?.checkout_url;
      if (url) {
        toast.success('Redirection vers Stripe…');
        window.location.href = url;
      } else {
        toast.error('Lien Stripe indisponible');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur paiement Stripe');
    } finally {
      setBusyPay(null);
    }
  };

  // V37.76 — Paiement avec les credits du compte.
  const handlePayCredits = async (project) => {
    setBusyPayCredits(project.id);
    try {
      const requestId = await ensureRequestId(project);
      if (!requestId) {
        toast.error('Impossible de créer la demande de paiement');
        return;
      }
      const res = await axios.post(
        `${API}/legalcoop/requests/${requestId}/pay-with-credits`,
        {},
        { headers: getAuthHeader() },
      );
      if (res.data?.role_exempt) {
        toast.success('Service exonéré (règle de rôle) — aucun crédit débité');
      } else {
        toast.success(`Payé avec ${res.data?.credits_spent} crédits — nouveau solde : ${res.data?.balance_after}`);
      }
      await fetchList();
      await fetchPayOptions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur paiement par crédits');
    } finally {
      setBusyPayCredits(null);
    }
  };

  const handleDownloadFinal = async (project) => {
    const docId = project?.final_contract?.generated_document_id;
    if (!docId) return;
    setBusyDownload(project.id);
    try {
      const res = await axios.get(
        `${API}/ccmi/projects/${project.id}/final-contract/${docId}`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cn = res.headers?.['x-contract-number'] || project?.final_contract?.generated_contract_number || 'contrat-ccmi';
      a.download = `${cn}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Contrat définitif téléchargé');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur téléchargement');
    } finally {
      setBusyDownload(null);
    }
  };

  if (loading) {
    return (
      <Card data-testid="mes-contrats-ccmi-loading">
        <CardContent className="py-6 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement de vos contrats CCMI...
        </CardContent>
      </Card>
    );
  }

  if (!items.length) {
    // n'affiche rien si le membre n'a pas de projet CCMI a son nom
    return null;
  }

  const creditsAvailable = !!payOptions?.credits_enabled;
  const creditsSufficient = !!payOptions?.credits_sufficient;
  const creditBalance = payOptions?.credit_balance ?? 0;
  const priceCredits = payOptions?.price_credits;

  return (
    <Card data-testid="mes-contrats-ccmi">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Home className="h-5 w-5 text-emerald-700" />
          Vos contrats CCMI
        </CardTitle>
        <p className="text-xs text-slate-500">
          Vos projets de construction CCMI. Réglez en ligne le forfait
          <span className="font-semibold"> Contrat CCMI définitif imprimable — 500 € HT</span>
          {' '}pour débloquer la génération automatique.
        </p>
        {creditsAvailable && (
          <p className="text-[11px] text-amber-700" data-testid="mes-contrats-ccmi-credit-balance">
            <Coins className="h-3 w-3 inline mr-1" />
            Solde crédits : <b>{creditBalance}</b>
            {priceCredits != null && <> • Prix du forfait : <b>{priceCredits} crédits</b></>}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((p) => {
            const fc = p.final_contract || {};
            const statColor = STATUT_COLOR[p.statut] || STATUT_COLOR.brouillon;
            const hasGenerated = !!fc.generated_document_id;
            const isPaid = !!fc.paid;
            const isPending = !!fc.pending_request_id;
            return (
              <div
                key={p.id}
                className="border border-slate-200 rounded p-3 hover:bg-slate-50 transition-colors"
                data-testid={`mes-contrats-ccmi-item-${p.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-[10px] ${statColor}`}>
                        {p.statut}
                      </Badge>
                      <span className="font-mono text-xs text-slate-700">
                        {p.reference}
                      </span>
                      {isPaid && (
                        <Badge className="text-[10px] bg-emerald-700 text-white border-emerald-900 font-semibold"
                          data-testid={`mes-contrats-ccmi-paid-${p.id}`}>
                          Forfait contrat définitif payé
                        </Badge>
                      )}
                      {!isPaid && isPending && (
                        <Badge className="text-[10px] bg-amber-600 text-white border-amber-800 font-semibold">
                          Paiement en attente
                        </Badge>
                      )}
                      {hasGenerated && (
                        <span className="text-[10px] text-emerald-800 font-semibold">
                          {fc.generated_contract_number}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mt-1">
                      {p.type_contrat ? `Type ${p.type_contrat}` : null}
                      {p.prix_ttc ? <> • {Number(p.prix_ttc).toLocaleString('fr-FR')} € TTC</> : null}
                      {p.maitre_ouvrage_nom ? <> • Maître d&apos;ouvrage <b>{p.maitre_ouvrage_nom}</b></> : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {hasGenerated && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs bg-emerald-50 border-emerald-200 text-emerald-800"
                        onClick={() => handleDownloadFinal(p)}
                        disabled={busyDownload === p.id}
                        data-testid={`mes-contrats-ccmi-download-${p.id}`}
                      >
                        {busyDownload === p.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                        Télécharger
                      </Button>
                    )}
                    {!isPaid && (
                      <>
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-blue-700 hover:bg-blue-800 text-white"
                          onClick={() => handlePayDirect(p)}
                          disabled={busyPay === p.id || busyPayCredits === p.id}
                          data-testid={`mes-contrats-ccmi-pay-${p.id}`}
                        >
                          {busyPay === p.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CreditCard className="h-3 w-3 mr-1" />}
                          {isPending ? 'Reprendre paiement (carte)' : 'Payer par carte (Stripe)'}
                        </Button>
                        {creditsAvailable && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                            onClick={() => handlePayCredits(p)}
                            disabled={busyPay === p.id || busyPayCredits === p.id || !creditsSufficient}
                            title={!creditsSufficient ? `Solde insuffisant (${creditBalance} crédits)` : ''}
                            data-testid={`mes-contrats-ccmi-pay-credits-${p.id}`}
                          >
                            {busyPayCredits === p.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Coins className="h-3 w-3 mr-1" />}
                            Payer avec mes crédits ({creditBalance})
                          </Button>
                        )}
                      </>
                    )}
                    {isPaid && !hasGenerated && (
                      <span className="text-[11px] text-slate-500 italic flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        Génération en attente du juriste
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-400 italic mt-3">
          Programme LEGALCOOP hébergé par OBJECTIF SCOP OUTREMER SCIC SAS — paiement sécurisé Stripe ou par crédits.
        </p>
      </CardContent>
    </Card>
  );
}
